import { getSettingSalesforceFallbackSalesRepCalendarUrl } from "@faire/retailer-visitor-shared/settings/getSettingSalesforceFallbackSalesRepCalendarUrl";
import { useBrandStore } from "@faire/retailer-visitor-shared/stores/domain/BrandStore";
import {
  trackInviteClickedOnce,
  useMakerSignUp,
} from "@faire/retailer-visitor-shared/views/apply/MakerApplication/useMakerSignUp";
import { MakerApplicationStages } from "@faire/retailer-visitor-shared/views/apply/MakerApplication/useMakerSignUpForm";
import { IS_PERSONALIZED_LANDING_PAGE_BRAND_TOKEN } from "@faire/retailer-visitor-shared/views/brands/landing-page/const";
import { useBrandsLandingPageStore } from "@faire/retailer-visitor-shared/views/brands/landing-page/stores/BrandsLandingPageContextStore";
import { logError } from "@faire/web/common/logging";
import { Storage } from "@faire/web/common/Storage";
import postProspectiveBrand from "@faire/web-api--source/endpoints/www/api/brand-lead/prospective-brand/post";
import getBrandSalesforceOwner from "@faire/web-api--source/endpoints/www/api/brand/brandToken/salesforce-owner/get";
import { IBrand } from "@faire/web-api--source/indigofair/data/IBrand";
import brandApplication from "@faire/web-api--source/routes/www/apply";
import qs from "qs";
import { useState, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";

import { useMakerSignUpForm } from "../../../apply/MakerApplication/useMakerSignUpForm";

interface BrandsLandingPageState {
  showBanner: boolean;
  email: string | undefined;
  brand: IBrand | undefined;
  isLoading: boolean;
  salesforceOwnerUrl: string | undefined;
  isMarketSectionVisible: boolean;
  handleEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  emailAddress: string | undefined;
  calendlyLink: string;
  init: () => Promise<void>;
  isValidBrandToken: boolean;
  isPersonalized: boolean;
  isR2BorB2B: boolean;
  goToForm: () => void;
  setIsMarketSectionVisible: (isVisible: boolean) => void;
  setBanner: (isVisible: boolean) => void;
}

export const useBrandsLandingPageState = (): BrandsLandingPageState => {
  const history = useHistory();

  const [
    {
      brandToken,
      existingEmail,
      showBanner,
      email,
      isLoading,
      salesforceOwnerUrl,
      isMarketSectionVisible,
    },
    setBrandsLandingPageStore,
  ] = useBrandsLandingPageStore([
    "brandToken",
    "existingEmail",
    "showBanner",
    "email",
    "isLoading",
    "salesforceOwnerUrl",
    "isMarketSectionVisible",
  ]);

  const {
    isReferral,
    setApplicationStage,
    isM2M,
    retailerTokenFromPath,
    brandTokenOrAlias,
  } = useMakerSignUp();

  const { email: makerSignUpFormEmail, setEmailAddress } = useMakerSignUpForm();

  const { prefetchedFullBrand, getBrand } = useBrandStore([
    "prefetchedFullBrand",
    "getBrand",
  ]);

  const [brand, setBrand] = useState<IBrand | undefined>(prefetchedFullBrand);

  const preSavedBrandToken = Storage.local.getItem(
    IS_PERSONALIZED_LANDING_PAGE_BRAND_TOKEN
  );

  const fetchCalenderUrl = useCallback(
    async (brandToken: string): Promise<void> => {
      try {
        const res = await getBrandSalesforceOwner(brandToken);
        setBrandsLandingPageStore({
          salesforceOwnerUrl: res.account_owner?.calendar_url,
        });
      } catch (error) {
        logError(error);
      }
    },
    [setBrandsLandingPageStore]
  );

  const loadBrand = useCallback(async (): Promise<void> => {
    const activeOrSavedBrandToken = brandToken ?? preSavedBrandToken;
    if (!activeOrSavedBrandToken || brand) {
      return;
    }

    try {
      setBrandsLandingPageStore({ isLoading: true });
      await fetchCalenderUrl(activeOrSavedBrandToken);
      const brandData = await getBrand(activeOrSavedBrandToken);
      setBrand(brandData);
    } catch (error) {
      logError(error);
    } finally {
      setBrandsLandingPageStore({ isLoading: false });
    }
  }, [
    brand,
    brandToken,
    fetchCalenderUrl,
    getBrand,
    preSavedBrandToken,
    setBrandsLandingPageStore,
  ]);

  const storePersonalizedLandingPage = useCallback(() => {
    if (brandToken && brand) {
      Storage.local.setItem(
        IS_PERSONALIZED_LANDING_PAGE_BRAND_TOKEN,
        brandToken
      );
    }
  }, [brandToken, brand]);

  const init = useCallback(async () => {
    await loadBrand();
    storePersonalizedLandingPage();
  }, [loadBrand, storePersonalizedLandingPage]);

  const emailAddress = useMemo(
    () => email || makerSignUpFormEmail || existingEmail,
    [email, makerSignUpFormEmail, existingEmail]
  );

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setBrandsLandingPageStore({ email: e.target.value });
    },
    [setBrandsLandingPageStore]
  );

  const calendlyLink =
    salesforceOwnerUrl ?? getSettingSalesforceFallbackSalesRepCalendarUrl();

  const isValidBrandToken = !!brandToken && !!brand;

  const isPersonalized = !!preSavedBrandToken || isValidBrandToken;

  const isR2BorB2B = isReferral;

  const applyEnteredEmail = useCallback(() => {
    if (!emailAddress) {
      return;
    }

    setEmailAddress(emailAddress);
    trackInviteClickedOnce(
      isM2M,
      retailerTokenFromPath,
      emailAddress,
      brandTokenOrAlias
    );

    postProspectiveBrand({
      email_address: emailAddress,
    });

    // Add the entered email to the query params to preserve information if they refresh
    history.replace({
      ...history.location,
      search: qs.stringify({
        ...qs.parse(history.location.search),
        email_address: emailAddress,
      }),
    });
  }, [
    brandTokenOrAlias,
    emailAddress,
    history,
    isM2M,
    retailerTokenFromPath,
    setEmailAddress,
  ]);

  const goToForm = () => {
    applyEnteredEmail();
    setApplicationStage(MakerApplicationStages.FORM);
    history.push(
      brandApplication(
        emailAddress ? { email_address: emailAddress } : undefined
      )
    );
  };

  const setIsMarketSectionVisible = (value: boolean) => {
    setBrandsLandingPageStore({ isMarketSectionVisible: value });
  };

  const setBanner = (value: boolean) => {
    setBrandsLandingPageStore({ showBanner: value });
  };

  return {
    showBanner,
    email,
    brand,
    isLoading,
    salesforceOwnerUrl,
    isMarketSectionVisible,
    handleEmailChange,
    emailAddress,
    calendlyLink,
    init,
    isValidBrandToken,
    isPersonalized,
    isR2BorB2B,
    goToForm,
    setIsMarketSectionVisible,
    setBanner,
  };
};
